import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  Grid,
  useMediaQuery,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { fetchOfferData, createOffer, updateOffer, deleteOffer, fetchClientStores } from '../api';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Autocomplete } from '@mui/material';
import CheckBox from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';


dayjs.extend(utc);
dayjs.extend(timezone);

function OfferPage() {
  const [data, setData] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({ id: '', offerName: '', description: '', offerEndTime: dayjs(), offerStartTime: dayjs(), storeIds: [] });
  const [isEdit, setIsEdit] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false); // New state for dialog actions
  const [deletingId, setDeletingId] = useState(null); // State to track which item is being deleted

  const [stores, setStores] = useState([]);
  const [formErrors, setFormErrors] = useState({});


  useEffect(() => {
    const fetchStores = async () => {
      try {
        const apiResponse = await fetchClientStores(); // Adjust API path
        // if (response.ok) {
        //   const json = await response.json();
        //   setStores(json.data || []);
        // }
        if (apiResponse.code === 0)
          setStores(apiResponse.data);
        else
          setError(apiResponse.message || 'Failed to load data.');

      } catch (err) {
        console.error('Failed to fetch stores:', err);
      }
    };

    fetchStores();
  }, []);

  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const nameRef = useRef(null);
  const descriptionRef = useRef(null);

  const isAllStoresSelected = formData.storeIds.length === stores.length;
  const isStoreIndeterminate =
    formData.storeIds.length > 0 && !isAllStoresSelected;

  const handleStoreChange = (event) => {
    const value = event.target.value;

    if (value.includes('ALL')) {
      const allSelected = formData.storeIds.length === stores.length;
      handleInputChangeValue(
        'storeIds',
        allSelected ? [] : stores.map((s) => s.id)
      );
    } else {
      handleInputChangeValue('storeIds', value);
    }
  };


  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const apiResponse = await fetchOfferData();
        if (apiResponse.code === 0)
          setData(apiResponse.data);
        else
          setError(apiResponse.message || 'Failed to load data.');
      } catch (err) {
        setError(err.message || 'Failed to load data.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleOpenDialog = () => {

    setOpenDialog(true);
    const now = dayjs();
    setFormData({ id: '', offerName: '', description: '', offerEndTime: now.add(1, 'hour'), offerStartTime: now, storeIds: [] });
    setIsEdit(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setActionLoading(false); // Reset action loading on close

  };

  const validateForm = () => {
    const errors = {};

    if (!formData.offerName || formData.offerName.trim() === '') {
      errors.offerName = 'Offer name is required.';
    }

    if (!formData.description || formData.description.trim() === '') {
      errors.description = 'Description is required.';
    }

    if (!formData.offerStartTime) {
      errors.offerStartTime = 'Offer start time is required.';
    }

    if (!formData.offerEndTime) {
      errors.offerEndTime = 'Offer end time is required.';
    }

    if (
      formData.offerStartTime &&
      formData.offerEndTime &&
      formData.offerEndTime.isBefore(formData.offerStartTime)
    ) {
      errors.offerEndTime = 'Offer end time must be after start time.';
    }

    if (formData.offerScope === 'SPECIFIC_STORES') {
      if (!formData.storeIds || formData.storeIds.length === 0) {
        errors.storeIds = 'Please select at least one store.';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };


  const handleInputChangeValue = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleInputChangeGeneric = (eventOrValue, name) => {
    const value = (typeof eventOrValue === 'object' && eventOrValue !== null && typeof eventOrValue.target !== 'undefined')
      ? eventOrValue.target.value
      : eventOrValue;

    handleInputChangeValue(name, value);
  };

  const handleInputChange = (e) => {
    handleInputChangeGeneric(e, e.target.name);
  };

  const handleDateTimeChange = (date, field) => {
    handleInputChangeGeneric(date, field);
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    setActionLoading(true); // Start dialog action loading

    if (formData.offerEndTime.isBefore(formData.offerStartTime)) {
      setError("Offer end time must be after start time.");
      setActionLoading(false);
      return;
    }


    try {
      const newOffer = await createOffer({
        data: {
          offerName: formData.offerName,
          description: formData.description,
          offerStartTime: formData.offerStartTime.toISOString(),
          offerEndTime: formData.offerEndTime.toISOString(),
          active: true,
          offerScope: formData.offerScope,
          storeIds: formData.offerScope === 'SPECIFIC_STORES' ? formData.storeIds : [],
        },
      });

      console.log('new offewr' + JSON.stringify(newOffer));
      setData((prevData) => [...prevData, {...newOffer.data,storeIds: newOffer.data.storeIds || [] }]);
      handleCloseDialog();
    } catch (err) {
      setError(err.message || 'Failed to create offer.');
    } finally {
      setActionLoading(false); // End dialog action loading
    }
  };

  const handleEdit = (id) => {
    const offerToEdit = data.find((offer) => offer.id === id);
    if (offerToEdit) {
      setFormData({
        ...offerToEdit,
        offerStartTime: dayjs(offerToEdit.offerStartTime),
        offerEndTime: dayjs(offerToEdit.offerEndTime),
        storeIds: offerToEdit.offerScope === 'SPECIFIC_STORES' ? offerToEdit.storeIds : [],
        offerScope: offerToEdit.offerScope || ''
      });
      setSelectedId(id);
      setIsEdit(true);
      setOpenDialog(true);
    }
  };

  const formatDate = (value) => {
    return value && dayjs(value).isValid()
      ? dayjs(value).format('YYYY-MM-DD HH:mm:ss')
      : '';
  };


  const handleUpdate = async () => {
    if (!validateForm()) return;

    setActionLoading(true); // Start dialog action loading

    if (formData.offerEndTime.isBefore(formData.offerStartTime)) {
      setError("Offer end time must be after start time.");
      setActionLoading(false);
      return;
    }


    try {
      await updateOffer(selectedId, {
        data: {
          offerName: formData.offerName,
          description: formData.description,
          offerStartTime: formData.offerStartTime.toISOString(),
          offerEndTime: formData.offerEndTime.toISOString(),
          active: true,
          offerScope: formData.offerScope,
          storeIds: formData.offerScope === 'SPECIFIC_STORES' ? formData.storeIds : [],
        },
      });
      const updatedData = data.map((offer) =>
        offer.id === selectedId ? { ...formData, id: selectedId } : offer
      );
      setData(updatedData);
      handleCloseDialog();
    } catch (err) {
      setError(err.message || 'Failed to update Offer.');
    } finally {
      setActionLoading(false); // End dialog action loading
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id); // Set deleting state for this specific item

    try {
      await deleteOffer(id);
      const updatedData = data.filter((offer) => offer.id !== id);
      setData(updatedData);
    } catch (err) {
      setError(err.message || 'Failed to delete offer.');
    } finally {
      setDeletingId(null); // Clear deleting state
    }
  };

  if (loading) {
    return <Typography align="center" mt={5}>Loading...</Typography>;
  }

  if (error) {
    return <Typography color="error" align="center" mt={5}>Error: {error}</Typography>;
  }

  return (
    <Box sx={{ p: 3, mt: 3 }}>
      <Typography variant="h4" gutterBottom>
        Offers
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Button variant="contained" color="primary" onClick={handleOpenDialog}>
          Create New
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: isMobile ? 300 : 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>End Time</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.offerName}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{formatDate(row.offerStartTime)}</TableCell>
                <TableCell>{formatDate(row.offerEndTime)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={() => handleEdit(row.id)} size="small" variant="outlined"
                      disabled={actionLoading || deletingId !== null} // Disable if dialog action or delete in progress
                    >
                      <EditIcon />
                    </Button>
                    <Button onClick={() => handleDelete(row.id)} size="small" variant="outlined" color="error"
                      disabled={actionLoading || deletingId === row.id} // Disable if dialog action or *this* item is being deleted
                      startIcon={deletingId === row.id ? <CircularProgress size={16} color="inherit" /> : null} // Show spinner if deleting this item
                    >
                      {deletingId === row.id ? 'Deleting...' : <DeleteIcon />} {/* Change text/icon */}
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        scroll="body"
        PaperProps={{
          sx: {
            overflow: 'visible',
          },
        }}
      >
        <DialogTitle>{isEdit ? 'Edit Offer' : 'Create New Offer'}</DialogTitle>
        <DialogContent
          sx={{
            overflow: 'visible',
            position: 'relative',
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                margin="dense"
                name="offerName"
                label="Name"
                type="text"
                fullWidth
                value={formData.offerName}
                onChange={handleInputChange}
                inputRef={nameRef}
                disabled={actionLoading} // Disable input while dialog action is loading
                error={!!formErrors.offerName}
                helperText={formErrors.offerName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="description"
                label="Description"
                type="text"
                fullWidth
                value={formData.description}
                onChange={handleInputChange}
                inputRef={descriptionRef}
                disabled={actionLoading} // Disable input while dialog action is loading
                error={!!formErrors.description}
                helperText={formErrors.description}

              />
            </Grid>
            <Grid item xs={12} sx={{ position: 'relative', zIndex: 1300 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Offer Start Time"
                  value={formData.offerStartTime}
                  helperText={formErrors.offerStartTime}
                  error={!!formErrors.offerStartTime}
                  onChange={(date) => handleDateTimeChange(date, "offerStartTime")}
                  disabled={actionLoading} // Disable picker itself
                  slotProps={{
                    textField: {
                      margin: 'dense',
                      fullWidth: true,
                    },
                    popper: {
                      disablePortal: true, // 🔥 keeps popup within dialog
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          options: {
                            boundary: 'clippingParents',
                            padding: 8,
                          },
                        },
                      ],
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sx={{ position: 'relative', zIndex: 1300 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Offer End Time"
                  disabled={actionLoading} // Disable picker itself
                  value={formData.offerEndTime}
                  helperText={formErrors.offerEndTime}
                  error={!!formErrors.offerEndTime}
                  onChange={(date) => handleDateTimeChange(date, "offerEndTime")}
                  slotProps={{
                    textField: {
                      margin: 'dense',
                      fullWidth: true,
                    },
                    popper: {
                      disablePortal: true, // 🔥 keeps popup within dialog
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          options: {
                            boundary: 'clippingParents',
                            padding: 8,
                          },
                        },
                      ],
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Offer Scope"
                name="offerScope"
                value={formData.offerScope}
                onChange={handleInputChange}
                SelectProps={{
                  native: false,
                }}
                disabled={actionLoading}
              >
                <MenuItem value="ONLINE_NATIONWIDE">Online</MenuItem>
                <MenuItem value="SPECIFIC_STORES">Specific Stores</MenuItem>
              </TextField>
            </Grid>

            {formData.offerScope === 'SPECIFIC_STORES' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select Stores</InputLabel>
                  <Select
                    multiple
                    value={formData.storeIds}
                    onChange={handleStoreChange}
                    input={<OutlinedInput label="Select Stores" />}
                    renderValue={(selected) => {
                      if (selected.length === 0) return 'Select Stores';
                      const selectedNames = stores
                        .filter((s) => selected.includes(s.id))
                        .map((s) => s.storeName);
                      return selectedNames.join(', ');
                    }}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                  >
                    <MenuItem value="ALL">
                      <Checkbox checked={isAllStoresSelected} />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {stores.map((store) => (
                      <MenuItem key={store.id} value={store.id}>
                        <Checkbox checked={formData.storeIds.includes(store.id)} />
                        <ListItemText primary={store.storeName} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">Cancel</Button>
          <Button onClick={isEdit ? handleUpdate : handleCreate} color="primary" variant="contained"
            disabled={actionLoading} // Disable button while loading
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : null} // Show spinner
          >
            {actionLoading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update' : 'Create')} {/* Change text */}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OfferPage;
